import { ClipboardList, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { EmailAutocomplete } from "./EmailAutocomplete";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { AssigneeOptions } from "./transactions/AssigneeOptions";

const PARTIES = ["assigned_coordinator", "manager", "buyer", "buyer2", "seller", "seller2", "buyer_agent", "seller_agent", "lender", "title_company", "loan_processor"];

const AddTaskForm = ({ newTask, setNewTask, onSubmit, onCancel, activeUsers = [], currentUserId, InsertVariableBtn, contacts = [], transactionPeople = [] }) => {
  return (
    <div className="border-b border-[var(--t-border)] bg-[var(--t-bg)] px-5 py-4 space-y-3" data-testid="add-task-form">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <Label className="text-xs mb-1">Type</Label>
          <Select value={newTask.task_type} onValueChange={(v) => setNewTask({ ...newTask, task_type: v })}>
            <SelectTrigger className="h-8 text-sm" data-testid="new-task-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="task"><span className="flex items-center gap-1.5"><ClipboardList className="w-3.5 h-3.5" /> Task</span></SelectItem>
              <SelectItem value="email"><span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Email</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs mb-1">Task Title</Label>
          <Input
            placeholder="Task name"
            value={newTask.title}
            onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            className="h-8 text-sm"
            data-testid="new-task-title"
          />
        </div>
        <div>
          <Label className="text-xs mb-1">Due Date</Label>
          <Input
            type="date"
            value={newTask.due_date}
            onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
            className="h-8 text-sm"
            data-testid="new-task-due-date"
          />
        </div>
        <div>
          <Label className="text-xs mb-1">Responsible Party</Label>
          <Select
            value={newTask.assigned_to_type || "none"}
            onValueChange={(v) => setNewTask({ ...newTask, assigned_to_type: v === "none" ? "" : v })}
          >
            <SelectTrigger className="h-8 text-sm" data-testid="new-task-role">
              <SelectValue placeholder="Select role..." />
            </SelectTrigger>
            <SelectContent>
              <AssigneeOptions activeUsers={activeUsers} currentUserId={currentUserId} />
            </SelectContent>
          </Select>
        </div>
      </div>
      {newTask.task_type === "email" && (
        <div className="space-y-3 p-3 rounded-md bg-blue-50/60 border border-blue-200">
          <Label className="text-xs font-semibold flex items-center gap-1.5 text-blue-700">
            <Mail className="w-3.5 h-3.5" /> Email Template
          </Label>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">Send To</Label>
            <div className="flex flex-wrap gap-2">
              {PARTIES.map((party) => (
                <label key={party} className="flex items-center gap-1 cursor-pointer">
                  <Checkbox
                    checked={(newTask.email_recipients || []).includes(party)}
                    onCheckedChange={() => {
                      const cur = newTask.email_recipients || [];
                      setNewTask({ ...newTask, email_recipients: cur.includes(party) ? cur.filter(r => r !== party) : [...cur, party] });
                    }}
                    data-testid={`new-email-recipient-${party}`}
                  />
                  <span className="text-xs capitalize">{party.replace(/_/g, " ")}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">CC</Label>
              <EmailAutocomplete
                value={newTask.email_cc || ""}
                onChange={(val) => setNewTask({ ...newTask, email_cc: val })}
                contacts={contacts}
                transactionPeople={transactionPeople}
                placeholder="Type to search contacts..."
                className="h-8 text-sm bg-white"
                data-testid="new-email-cc"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)] mb-1 block">BCC</Label>
              <EmailAutocomplete
                value={newTask.email_bcc || ""}
                onChange={(val) => setNewTask({ ...newTask, email_bcc: val })}
                contacts={contacts}
                transactionPeople={transactionPeople}
                placeholder="Type to search contacts..."
                className="h-8 text-sm bg-white"
                data-testid="new-email-bcc"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">Subject</Label>
              <InsertVariableBtn onInsert={(v) => setNewTask(prev => ({ ...prev, email_subject: (prev.email_subject || "") + " " + v }))} />
            </div>
            <Input
              value={newTask.email_subject || ""}
              onChange={(e) => setNewTask({ ...newTask, email_subject: e.target.value })}
              placeholder="e.g., Wire Fraud Warning - [Property Address]"
              className="h-8 text-sm bg-white"
              data-testid="new-email-subject"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-[10px] uppercase tracking-wider text-[var(--t-text-muted)]">Email Body</Label>
              <InsertVariableBtn onInsert={(v) => setNewTask(prev => ({ ...prev, email_body: (prev.email_body || "") + v }))} />
            </div>
            <Textarea
              value={newTask.email_body || ""}
              onChange={(e) => setNewTask({ ...newTask, email_body: e.target.value })}
              placeholder={"Dear [Buyer],\n\nThis is a reminder regarding..."}
              rows={4}
              className="text-sm bg-white font-mono"
              data-testid="new-email-body"
            />
            <p className="text-[10px] text-blue-600 mt-1">
              Variables: [Buyer], [Seller], [Lender], [Property Address], [Closing Date], etc.
            </p>
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="cancel-add-task">
          Cancel
        </Button>
        <Button size="sm" onClick={onSubmit} disabled={!newTask.title || !newTask.due_date} className="bg-[var(--t-primary)] hover:bg-[var(--t-primary-hover)]" data-testid="save-add-task">
          Add Task
        </Button>
      </div>
    </div>
  );
};

export default AddTaskForm;
